<script setup>
// SSR-safe Teleport: при включённом SSR Teleport ломает гидрацию поддерева,
// поэтому модалку рендерим только на клиенте (после mount).
import { ref as __ref, onMounted as __onMounted } from 'vue';
import PerkList from "@/Components/PerkList.vue";
const __teleportReady = __ref(false);
__onMounted(() => { __teleportReady.value = true; });

import MainHeader from "@/Layouts/MainHeader.vue";
import ReviewCarousel from "@/Layouts/ReviewCarousel.vue";
import Articles from "@/Layouts/Articles.vue";
import LawyerAnswers from "@/Layouts/LawyerAnswers.vue";
import About from "@/Layouts/About.vue";
import UslugaDocuments from "@/Layouts/UslugaDocuments.vue";
import MainFooter from "@/Layouts/MainFooter.vue";
import Tracking from '@/Components/ResourseLoader.vue';
import FlashMessage from "@/Components/FlashMessage.vue";
import { Head, Link } from "@inertiajs/vue3";
import { ref, computed, onMounted, onUnmounted } from 'vue';

const set = defineProps({
  lawyer: Object,
  perks: { type: Array, default: () => [] },
  photos: { type: Array, default: () => [] },
  addresses: { type: Array, default: () => [] },
  current_city_id: { type: Number, default: 0 },
  articles: Object,
  anwswers: Object,
  practice: Array,
  documents: { type: Array, default: () => [] },
  flash: Object,
  auth: Object,
  countarticles: Number,
  errors: Object,
  backendurl: String,
});

// noindex, если не юрист или скрыт из фида (такие в sitemap не попадают)
const robots = set.lawyer.lawyer !== 1 || Number(set.lawyer.show_in_feed) !== 1;

const cityTitle = computed(() => {
  const addr = set.addresses?.find(a => a.city);
  return addr?.city?.title ?? set.lawyer.city ?? null;
});

const metaTitle = computed(() =>
  cityTitle.value
    ? `Юрист ${set.lawyer.name} — ${cityTitle.value} | nedicom.ru`
    : `Юрист ${set.lawyer.name} | nedicom.ru`
);

const metaDescription = computed(() => {
  const base = set.lawyer.about ?? set.lawyer.name;
  return base.length > 160 ? base.slice(0, 157) + '…' : base;
});

// Телефон для боковой панели и мобильной панели
const visibleAddresses = computed(() => {
  if (!set.addresses?.length) return [];
  const matched = set.addresses.filter(a => a.city_id && a.city_id === set.current_city_id);
  if (matched.length) return matched;
  return set.addresses.filter(a => !a.city_id);
});

const displayPhone = computed(() =>
  visibleAddresses.value.find(a => a.phone)?.phone || set.lawyer.phone || null
);

const maskedPhone = computed(() =>
  displayPhone.value ? displayPhone.value.replace(/\d{2}$/, '**') : null
);

const showPhoneModal = ref(false);

function openPhoneModal() {
  showPhoneModal.value = true;
  try { window.goal('LAWYER_PHONE'); } catch (e) {}
}

// Показывать мобильную панель после прокрутки
const showMobileBar = ref(false);
function onScroll() {
  showMobileBar.value = window.scrollY > 200;
}
onMounted(() => window.addEventListener('scroll', onScroll, { passive: true }));
onUnmounted(() => window.removeEventListener('scroll', onScroll));

const hasReviews = computed(() => !!set.lawyer.reviews_sum_rating);

// Средняя оценка: сумма делится на количество. Готового поля нет, а в полосе
// цифр звезда без числа смотрелась бы обещанием без содержания.
const avgRating = computed(() => {
  const sum = Number(set.lawyer.reviews_sum_rating) || 0;
  const n = Number(set.lawyer.reviews_count) || 0;
  return n ? sum / n : 0;
});
const hasArticles = computed(() => set.countarticles > 0);
const hasPractice = computed(() => set.practice?.length > 0);
const hasAnswers = computed(() => set.anwswers?.length > 0);
const hasDocuments = computed(() => set.documents?.length > 0);

const canEdit = computed(() =>
  set.auth && (set.auth.id === set.lawyer.id || set.auth.isadmin)
);
</script>

<template>
  <FlashMessage :message="flash.message" />

  <Head>
    <title>{{ metaTitle }}</title>
    <meta head-key="description" name="description" :content="metaDescription" />
    <meta name="robots" :content="robots ? 'noindex,follow' : 'index,follow'" />
    <meta property="og:title" :content="metaTitle" />
    <meta property="og:description" :content="metaDescription" />
    <meta property="og:type" content="profile" />
    <meta property="og:url" :content="'https://nedicom.ru/lawyers/' + set.lawyer.id" />
    <meta v-if="set.lawyer.avatar_path" property="og:image" :content="'https://nedicom.ru/' + set.lawyer.avatar_path" />
    <meta property="og:site_name" content="nedicom.ru" />
    <meta property="og:locale" content="ru_RU" />
    <link rel="canonical" :href="'https://nedicom.ru/lawyers/' + set.lawyer.id" />
  </Head>

  <MainHeader :auth="auth" />

  <div class="min-h-screen bg-gray-50">
    <div class="max-w-7xl mx-auto px-4 py-6">
      <div class="grid grid-cols-1 lg:grid-cols-4 gap-6">

        <!-- Sidebar -->
        <aside class="lg:col-span-1 order-2 lg:order-1">
          <div class="sticky top-16 space-y-3">

            <!-- Nav карточка -->
            <nav class="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
              <div class="px-4 py-3 border-b border-gray-100 flex items-center gap-3">
                <img
                  v-if="set.lawyer.avatar_path"
                  :src="'https://nedicom.ru/' + set.lawyer.avatar_path"
                  :alt="set.lawyer.name"
                  loading="lazy"
                  class="w-9 h-9 rounded-full object-cover flex-shrink-0 ring-2 ring-indigo-100"
                />
                <span class="text-sm font-semibold text-gray-900 truncate">{{ set.lawyer.name }}</span>
              </div>
              <div class="p-2 space-y-0.5">
                <a href="#about"
                  class="flex items-center justify-between px-3 py-2 rounded-lg text-sm text-gray-600 hover:bg-indigo-50 hover:text-indigo-700 transition-colors group">
                  <span>О юристе</span>
                  <svg class="w-3.5 h-3.5 text-gray-300 group-hover:text-indigo-400 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
                  </svg>
                </a>
                <a v-if="hasReviews" href="#otzivi"
                  class="flex items-center justify-between px-3 py-2 rounded-lg text-sm text-gray-600 hover:bg-indigo-50 hover:text-indigo-700 transition-colors group">
                  <span>Отзывы</span>
                  <span class="text-xs bg-indigo-100 text-indigo-600 rounded-full px-2 py-0.5 font-medium">{{ set.lawyer.reviews_count }}</span>
                </a>

                <a v-if="hasPractice" href="#practice"
                  class="flex items-center justify-between px-3 py-2 rounded-lg text-sm text-gray-600 hover:bg-indigo-50 hover:text-indigo-700 transition-colors group">
                  <span>Практика</span>
                  <span class="text-xs bg-indigo-100 text-indigo-600 rounded-full px-2 py-0.5 font-medium">{{ set.practice.length }}</span>
                </a>
                <a v-if="hasDocuments" href="#documents"
                  class="flex items-center justify-between px-3 py-2 rounded-lg text-sm text-gray-600 hover:bg-indigo-50 hover:text-indigo-700 transition-colors group">
                  <span>Документы</span>
                  <span class="text-xs bg-indigo-100 text-indigo-600 rounded-full px-2 py-0.5 font-medium">{{ set.documents.length }}</span>
                </a>
                <a v-if="hasAnswers" href="#answers"
                  class="flex items-center justify-between px-3 py-2 rounded-lg text-sm text-gray-600 hover:bg-indigo-50 hover:text-indigo-700 transition-colors group">
                  <span>Ответы</span>
                  <span class="text-xs bg-indigo-100 text-indigo-600 rounded-full px-2 py-0.5 font-medium">{{ set.anwswers.length }}</span>
                </a>
              </div>
            </nav>

            <!-- Телефон на десктопе -->
            <div v-if="displayPhone" class="hidden lg:block">
              <button
                type="button"
                @click="openPhoneModal"
                class="phone-lawyer-btn w-full inline-flex items-center justify-center gap-3 py-3 px-5 text-white
                       bg-brand-600 hover:bg-brand-700 rounded-xl shadow-lg
                       hover:scale-105 transition-all duration-300 cursor-pointer"
              >
                <svg class="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                    d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"/>
                </svg>
                <div class="flex flex-col items-start">
                  <span class="text-base font-bold whitespace-nowrap leading-tight">{{ maskedPhone }}</span>
                  <span class="text-xs">Показать номер</span>
                </div>
              </button>
            </div>

            <!-- Страница ВК юриста (поле «Страница ВКонтакте» в профиле). rel="me" — та же
                 связь «это я», что sameAs в микроразметке персоны -->
            <a
              v-if="set.lawyer.vk_url"
              :href="set.lawyer.vk_url"
              target="_blank"
              rel="me noopener nofollow"
              class="flex items-center justify-center gap-2 w-full px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <svg class="w-5 h-5 text-gray-500" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M12.8 17.5C6.9 17.5 3.5 13.4 3.4 6.6h2.9c.1 5 2.3 7.1 4.1 7.6V6.6h2.8v4.3c1.7-.2 3.5-2.1 4.1-4.3h2.7c-.5 2.6-2.4 4.5-3.8 5.3 1.4.7 3.6 2.4 4.4 5.6h-3c-.6-2-2.3-3.6-4.4-3.8v3.8h-.4z"/>
              </svg>
              Страница ВКонтакте
            </a>

            <!-- Ссылка для владельца -->
            <Link
              v-if="canEdit"
              :href="'/lawyers/' + set.lawyer.id + '/edit'"
              class="flex items-center justify-center gap-2 w-full px-4 py-2 text-sm font-medium text-indigo-600 bg-white border border-indigo-200 rounded-xl hover:bg-indigo-50 transition-colors"
            >
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/>
              </svg>
              Редактировать
            </Link>

          </div>
        </aside>

        <!-- Основной контент (микроразметка LegalService/отзывов вынесена в JSON-LD контроллера) -->
        <main
          class="lg:col-span-3 order-1 lg:order-2 bg-white rounded-2xl shadow-sm border border-gray-100 px-4 md:px-8"
        >
          <About
            :lawyer="set.lawyer"
            :photos="set.photos"
            :addresses="set.addresses"
            :current-city-id="set.current_city_id"
            :reviews-count="Number(set.lawyer.reviews_count) || 0"
            :rating="avgRating"
            :practice-count="set.practice?.length || 0"
            :answers-count="set.anwswers?.length || 0"
          />

          <!-- ЧЕМ ПОДТВЕРЖДЁН. Раньше здесь стоял ряд картинок, а объяснение
               открывалось наведением: текст лежал в разметке, но спрятанный, и
               человек без наведения не узнавал, за что значок. Теперь открыто —
               с личной историей выдачи там, где она записана. -->
          <section v-if="set.perks.length" id="perks" class="border-t border-gray-100 py-6 scroll-mt-24">
            <h2 class="text-lg font-bold text-gray-900 mb-4">Достижения</h2>
            <PerkList :perks="set.perks" />
          </section>

          <div v-if="hasReviews" id="otzivi" class="border-t border-gray-100 py-6 px-4 md:px-8">
            <ReviewCarousel
              :reviews="set.lawyer.reviews"
              :rating="set.lawyer.reviews_count > 0 ? Math.round(Number(set.lawyer.reviews_sum_rating) / set.lawyer.reviews_count * 10) / 10 : 0"
              :reviewscount="set.lawyer.reviews_count"
              :lwrid="set.lawyer.id"
              :auth="set.auth"
              :errors="set.errors"
              :mainuslugaid="null"
              :uslugaid="null"
            />
          </div>

          <div v-if="hasPractice" class="border-t border-gray-100 py-6 px-4 md:px-8" id="practice">
            <h2 class="font-semibold mb-4 text-xl tracking-tight text-gray-900">Практика юриста</h2>
            <div class="grid grid-cols-2 md:grid-cols-3 gap-3">
              <a v-for="card in set.practice" :key="card.id"
                :href="route('articles/url', card.url)" target="_blank"
                :aria-label="'практика юриста - ' + card.header"
                class="relative block w-full aspect-[3/4] rounded-lg shadow overflow-hidden group">
                <img class="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    :src="'https://nedicom.ru/' + card.practice_file_path" :alt="card.header">
                <div class="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent"></div>
                <div class="absolute bottom-0 left-0 right-0 p-3 flex flex-col gap-1">
                  <h4 class="text-sm font-bold text-white leading-snug line-clamp-2">{{ card.header }}</h4>
                  <p class="text-xs text-white/70 leading-relaxed line-clamp-3 mt-0.5">{{ card.description }}</p>
                  <span class="inline-flex items-center gap-1 text-xs text-white/80 font-medium mt-1">
                    подробнее
                    <svg class="w-3 h-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 14 10">
                      <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M1 5h12m0 0L9 1m4 4L9 9"/>
                    </svg>
                  </span>
                </div>
              </a>
            </div>
          </div>

          <UslugaDocuments :documents="set.documents" />

          <div v-if="hasAnswers" class="border-t border-gray-100 py-6">
            <LawyerAnswers :anwswers="set.anwswers" />
          </div>
        </main>

      </div>
    </div>
  </div>

  <!-- Мобильная панель (появляется после скролла) -->
  <Teleport to="body" v-if="__teleportReady">
    <Transition
      enter-active-class="transition duration-200 ease-out"
      enter-from-class="opacity-0 translate-y-4"
      enter-to-class="opacity-100 translate-y-0"
      leave-active-class="transition duration-150 ease-in"
      leave-from-class="opacity-100 translate-y-0"
      leave-to-class="opacity-0 translate-y-4"
    >
      <div v-if="showMobileBar && displayPhone"
        class="fixed bottom-0 inset-x-0 lg:hidden z-40 bg-white border-t border-gray-200 shadow-2xl"
      >
        <div class="flex items-center gap-3 px-4 py-3">
          <img
            v-if="set.lawyer.avatar_path"
            :src="'https://nedicom.ru/' + set.lawyer.avatar_path"
            :alt="set.lawyer.name"
            loading="lazy"
            class="w-10 h-10 rounded-full object-cover flex-shrink-0 ring-2 ring-indigo-100"
          />
          <div class="flex-1 min-w-0">
            <p class="text-sm font-semibold text-gray-900 truncate">{{ set.lawyer.name }}</p>
            <p class="text-xs text-gray-500">Юрист · nedicom.ru</p>
          </div>
          <button
            type="button"
            @click="openPhoneModal"
            class="phone-lawyer-btn flex-shrink-0 inline-flex items-center gap-2 py-2.5 px-4 text-white text-sm font-bold
                   bg-brand-600 hover:bg-brand-700 rounded-xl shadow-lg transition-all duration-300"
          >
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"/>
            </svg>
            Позвонить
          </button>
        </div>
      </div>
    </Transition>

    <!-- Модалка телефона (для сайдбара и мобильной панели) -->
    <Transition
      enter-active-class="transition duration-200 ease-out"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="transition duration-150 ease-in"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <div v-if="showPhoneModal"
        class="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50"
        @click.self="showPhoneModal = false"
      >
        <Transition
          enter-active-class="transition duration-200 ease-out"
          enter-from-class="opacity-0 translate-y-4"
          enter-to-class="opacity-100 translate-y-0"
        >
          <div v-if="showPhoneModal"
            class="w-full sm:max-w-sm bg-white rounded-t-3xl sm:rounded-2xl shadow-2xl overflow-hidden"
          >
            <div class="relative bg-gradient-to-b from-indigo-50 to-white pt-8 pb-4 px-6 flex flex-col items-center gap-3">
              <button class="absolute top-4 right-4 text-gray-500 hover:text-gray-600 transition-colors"
                @click="showPhoneModal = false">
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
                </svg>
              </button>
              <img v-if="set.lawyer.avatar_path"
                :src="'https://nedicom.ru/' + set.lawyer.avatar_path"
                :alt="set.lawyer.name"
                class="w-20 h-20 rounded-full object-cover ring-4 ring-white shadow-lg"
              />
              <p class="text-lg font-bold text-gray-900 text-center">{{ set.lawyer.name }}</p>
            </div>
            <div class="px-6 pb-6 flex flex-col gap-3">
              <p class="text-sm text-gray-500 text-center leading-relaxed">
                Первичная консультация — бесплатно.<br/>
                Работаем ежедневно, звоните в удобное время.
              </p>
              <a
                :href="'tel:' + displayPhone"
                onclick="window.goal('LAWYER_PHONE_CALL');"
                class="phone-lawyer-btn inline-flex w-full items-center justify-center gap-3 py-3 px-5 text-white
                       bg-brand-600 hover:bg-brand-700 rounded-xl shadow-lg
                       focus:outline-none focus:ring-4 focus:ring-brand-200 transition-colors duration-200"
              >
                <svg class="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                    d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"/>
                </svg>
                <span class="text-base font-bold whitespace-nowrap">{{ displayPhone }}</span>
              </a>
            </div>
          </div>
        </Transition>
      </div>
    </Transition>
  </Teleport>

  <MainFooter />

  <Tracking :key="backendurl" :tracking="$page.props.tracking" :backendurl="set.backendurl" />
</template>

<style scoped>
@keyframes pulse-ring {
  0%, 100% { box-shadow: 0 0 0 0 rgba(96, 165, 250, 0.6); }
  50%       { box-shadow: 0 0 0 10px rgba(96, 165, 250, 0); }
}
.phone-lawyer-btn {
  animation: pulse-ring 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
  box-shadow: 0 4px 20px rgba(79, 70, 229, 0.4);
}
</style>
